/**
 * inspections.ts — the monthly inspection, as `get_contract_inspections` hands it to a driver.
 *
 * ── THE SERVER WORKS EVERYTHING OUT; THIS FILE ONLY CHOOSES WHAT TO SHOW ─────
 * The round, the attendance, the late flag, the car's condition, the hold, the release and every
 * visit's result are derived in the database when the payload is read (D-T33: store facts, derive
 * state). Nothing here re-derives any of them. What lives here is the choice of which status card
 * applies — the first of the plan's seven that the `current` object says is true — the sentence
 * each card prints, the badge rule, which visits the record lists, and the labels and calendar
 * dates.
 *
 * ── A DRIVER SEES THEIR OWN CONTRACT AND NOTHING OF THE CAR'S HISTORY ────────
 * For a driver caller the function derives the payload from the lines graded on that contract
 * only (owner rulings 2026-10-15, "own inspections only" and "no car history to a new driver").
 * The car-level key `raised_before_contract`, which an admin or partner caller also gets, is left
 * out of `InspectionOpenItem` on purpose, so nothing in this app can render it.
 *
 * ── DATES ────────────────────────────────────────────────────────────────────
 * Every date in the payload is a `YYYY-MM-DD` Ghana calendar date. `parseISO` reads one as local
 * midnight of that calendar day, so `format` prints the same day in any phone's timezone;
 * `new Date('2026-12-07')` would be UTC midnight and print 6 Dec west of Greenwich.
 *
 * The payload types are written by hand: the function returns jsonb, which the generated Supabase
 * types can only call `Json`, so these interfaces are the contract. They follow
 * `20261015010000_a_monthly_inspection_says_what_must_be_fixed.sql`. [D-B62, D-T33]
 */

import { format, parseISO } from 'date-fns';

// ── The payload ──────────────────────────────────────────────────────────────

export type InspectionGrade = 'ok' | 'fix_soon' | 'fix_now' | 'fixed' | 'not_needed';
export type InspectionResult = 'passed' | 'passed_fix_soon' | 'held' | 'notes_only';
export type InspectionAttendance = 'not_owed' | 'not_yet' | 'presented' | 'held' | 'missed';
export type InspectionCondition = 'clean' | 'fix_soon' | 'held';
export type InspectionKind = 'monthly' | 'recheck';
export type InspectionSlot =
    | 'front_34'
    | 'driver_side'
    | 'rear_34'
    | 'passenger_side'
    | 'dashboard'
    | 'interior';

export interface InspectionChecklistLine {
    key: string;
    section: string;
    label: string;
    /** Advice for the inspector. Never graded from, and not shown to a driver. */
    guidance: string;
    position: number;
    added_on: string;
}

export interface InspectionChecklist {
    sections: Array<{ key: string; label: string; position: number }>;
    lines: InspectionChecklistLine[];
}

/** One item whose latest line on this contract is open (Fix soon or Must fix). */
export interface InspectionOpenItem {
    item: string;
    grade: 'fix_soon' | 'fix_now';
    note: string | null;
    line_id: number;
    inspection_id: number;
    /** The date of the run's first line on this contract. */
    raised_on: string;
    /** The run's lines on this contract, minus one. */
    times_carried: number;
    /** The photos of every line in the run. */
    photos: Array<{ id: number; url: string; line_id: number; inspection_id: number }>;
}

/**
 * This contract's place in the round whose span holds today. Dates are `YYYY-MM-DD`.
 *
 * Before the first round `period`, `opens_on`, `closes_on` and `attendance` are NULL and the
 * `next_*` keys name the first round. For a driver whose contract is not in progress today,
 * `condition`, `open_fix_now` and `open_fix_soon` are NULL.
 */
export interface InspectionCurrent {
    period: string | null;
    opens_on: string | null;
    closes_on: string | null;
    today: string;
    attendance: InspectionAttendance | null;
    late: boolean | null;
    condition: InspectionCondition | null;
    open_fix_now: number | null;
    open_fix_soon: number | null;
    /** The round's monthly on this contract: the first one dated inside the span. */
    inspection_id: number | null;
    inspected_on: string | null;
    result: InspectionResult | null;
    /** The visit in the span that took the car from held to not held. */
    released_inspection_id: number | null;
    released_on: string | null;
    next_period: string | null;
    next_opens_on: string | null;
    next_closes_on: string | null;
}

export interface InspectionVisitLine {
    id: number;
    item: string;
    grade: InspectionGrade;
    note: string | null;
    /** The item's grade at the visit before, among the visits in view. */
    previous_grade: InspectionGrade | null;
}

export interface InspectionVisitPhoto {
    id: number;
    url: string;
    slot: InspectionSlot | null;
    line_id: number | null;
}

export interface InspectionVisit {
    id: number;
    /** NULL for a legacy, notes-only row. */
    kind: InspectionKind | null;
    contract_id: number | null;
    between_contracts: boolean;
    inspection_at: string;
    inspected_on: string;
    /** Monthlies only: the round the visit counts for. */
    period: string | null;
    late: boolean | null;
    result: InspectionResult;
    released: boolean | null;
    odometer_km: number | null;
    previous_odometer_km: number | null;
    odometer_change_km: number | null;
    last_service_km: number | null;
    remarks: string | null;
    inspector_first_name: string | null;
    lines: InspectionVisitLine[];
    photos: InspectionVisitPhoto[];
}

export interface ContractInspections {
    contract_id: number;
    /**
     * Whose view the server built. A driver gets this contract's view; an admin, or a partner who
     * also drives their own car, gets the car's view, which lists every visit on the car.
     */
    viewer: 'admin' | 'partner' | 'driver';
    header: {
        vehicle_id: number | null;
        plate: string | null;
        make: string | null;
        model: string | null;
        driver_id: number | null;
        driver_name: string | null;
        roadworthy_expires_on: string | null;
        insurance_expires_on: string | null;
    };
    checklist: InspectionChecklist;
    open_items: InspectionOpenItem[];
    current: InspectionCurrent;
    /** Newest first. */
    visits: InspectionVisit[];
}

// ── The record ───────────────────────────────────────────────────────────────

/**
 * The visits this app lists: the contract's own. A driver's payload holds nothing else. When the
 * server sends the car's view instead (an admin, or a partner who also drives their own car), the
 * other contracts' visits and those between contracts are left out, so the driver app never
 * shows another driver's record; `current` and `open_items` stay the car's in that view, as the
 * server sent them. Legacy rows carry their contract, so they stay.
 */
export function visitsInView(inspections: Pick<ContractInspections, 'contract_id' | 'viewer' | 'visits'>): InspectionVisit[] {
    if (inspections.viewer === 'driver') return inspections.visits;
    return inspections.visits.filter((v) => v.contract_id === inspections.contract_id);
}

// ── The status card ──────────────────────────────────────────────────────────

/**
 * The card the Inspections section opens with: the first of the plan's states that `current`
 * says is true (PLAN.md §8), in this order — Held, Missed, Not yet, Released, Passed – fix soon,
 * Passed, Not owed. NULL when none of them applies, so no card shows.
 */
export type InspectionCardState =
    | { kind: 'held' }
    | { kind: 'missed'; period: string; closesOn: string }
    | { kind: 'not_yet'; opensOn: string; closesOn: string }
    | { kind: 'released'; releasedInspectionId: number; releasedOn: string }
    | { kind: 'passed_fix_soon'; inspectionId: number; inspectedOn: string; late: boolean }
    | { kind: 'passed'; inspectionId: number; inspectedOn: string; late: boolean }
    | { kind: 'not_owed'; nextOpensOn: string; nextClosesOn: string };

/**
 * Which status card applies, read from `current` alone.
 *
 * A NULL `condition` is the server saying this contract keeps no live state — for a driver, the
 * contract is not in progress today — so no card applies: a former driver is not told to bring a
 * car they no longer have (`missed` can outlive the contract when it ended after the window
 * closed). Before the first round there is no attendance, and the card names the first window.
 *
 * Passed and Passed – fix soon are the round's monthly's own result, as the server sent it, so
 * the card and that visit's certificate always agree. The open Fix soon lines the card lists are
 * the ones open now, which a later re-check may have changed.
 *
 * The card follows the payload whoever the viewer is. A partner who also drives their own car
 * gets the partner's view from the server (they own the car), so `condition` is the car's and is
 * never NULL for them; this function shows what that says.
 */
export function inspectionCardState(current: InspectionCurrent | null | undefined): InspectionCardState | null {
    if (!current || current.condition === null) return null;

    if (current.condition === 'held') return { kind: 'held' };

    if (current.attendance === 'missed' && current.period && current.closes_on) {
        return { kind: 'missed', period: current.period, closesOn: current.closes_on };
    }
    if (current.attendance === 'not_yet' && current.opens_on && current.closes_on) {
        return { kind: 'not_yet', opensOn: current.opens_on, closesOn: current.closes_on };
    }
    if (current.released_inspection_id !== null && current.released_on) {
        return { kind: 'released', releasedInspectionId: current.released_inspection_id, releasedOn: current.released_on };
    }
    if (current.inspection_id !== null && current.inspected_on
        && (current.result === 'passed' || current.result === 'passed_fix_soon')) {
        const visit = { inspectionId: current.inspection_id, inspectedOn: current.inspected_on, late: current.late === true };
        return { kind: current.result, ...visit };
    }

    const noRoundYet = current.period === null && current.attendance === null;
    if ((current.attendance === 'not_owed' || noRoundYet) && current.next_opens_on && current.next_closes_on) {
        return { kind: 'not_owed', nextOpensOn: current.next_opens_on, nextClosesOn: current.next_closes_on };
    }
    return null;
}

/**
 * The sentence a status card prints, word for word (PLAN.md §8; decision 5 owns the final
 * text). Kept here, out of the JSX, so the tests pin each one. The Passed sentences carry the
 * odometer of the round's monthly; the Late chip beside them is drawn by the card. The Held
 * sentence ends by saying the weekly instalment keeps running (owner ruling 2026-10-16, "yes it
 * keeps running"), with no amount or rate.
 */
export function inspectionCardMessage(
    state: InspectionCardState,
    visits: ReadonlyArray<Pick<InspectionVisit, 'id' | 'odometer_km'>>,
): string {
    switch (state.kind) {
        case 'held':
            return 'Your car is held at the yard until these are fixed. The repair is done by a workshop Primelane names; the car is released after a re-check. Your weekly instalment keeps running while the car is held.';
        case 'missed':
            return `You missed the ${formatRoundMonth(state.period)} inspection (due by ${formatDay(state.closesOn)}). Bring the car to the yard as soon as you can.`;
        case 'not_yet':
            return `Monthly inspection ${formatWindow(state.opensOn, state.closesOn)}. Bring the car to the yard.`;
        case 'released':
            return `Re-checked and released ${formatDay(state.releasedOn)}.`;
        case 'passed_fix_soon':
        case 'passed': {
            const km = visits.find((v) => v.id === state.inspectionId)?.odometer_km ?? null;
            return km === null
                ? `Inspected ${formatDay(state.inspectedOn)}`
                : `Inspected ${formatDay(state.inspectedOn)} · ${formatKm(km)}`;
        }
        case 'not_owed':
            return `First inspection: ${formatWindow(state.nextOpensOn, state.nextClosesOn)}.`;
    }
}

/**
 * The count on the Inspections menu item (PLAN.md §8): the open Must fix lines on this contract,
 * plus one while the car still has to come in — not held, and the round is Not yet or Missed.
 *
 * "Not held" is read as a live condition other than Held: a NULL condition is a contract the
 * server keeps no live state for (see `inspectionCardState`), and it never counts.
 */
export function inspectionBadgeCount(current: InspectionCurrent | null | undefined): number {
    if (!current || current.condition === null) return 0;
    const mustFix = current.open_fix_now ?? 0;
    const owesVisit = current.condition !== 'held'
        && (current.attendance === 'not_yet' || current.attendance === 'missed');
    return mustFix + (owesVisit ? 1 : 0);
}

// ── Labels ───────────────────────────────────────────────────────────────────

export const RESULT_LABEL: Record<InspectionResult, string> = {
    passed: 'Passed',
    passed_fix_soon: 'Passed – fix soon',
    held: 'Held',
    notes_only: 'Notes only',
};

export const GRADE_LABEL: Record<InspectionGrade, string> = {
    ok: 'OK',
    fix_soon: 'Fix soon',
    fix_now: 'Must fix',
    fixed: 'Fixed',
    not_needed: 'No fix needed',
};

/** The six fixed shots, in the order the inspector takes them. */
export const SLOT_ORDER: readonly InspectionSlot[] = [
    'front_34',
    'driver_side',
    'rear_34',
    'passenger_side',
    'dashboard',
    'interior',
];

export const SLOT_LABEL: Record<InspectionSlot, string> = {
    front_34: 'Front ¾',
    driver_side: 'Driver side',
    rear_34: 'Rear ¾',
    passenger_side: 'Passenger side',
    dashboard: 'Dashboard',
    interior: 'Interior',
};

/**
 * A line's grade as the re-grade choices name it (PLAN.md §3.4): an open line graded again reads
 * "Still must fix", "Down to fix soon", "Still fix soon" or "Now must fix". Every other line reads
 * its plain grade.
 */
export function lineGradeLabel(grade: InspectionGrade, previous: InspectionGrade | null): string {
    if (grade === 'fix_now') {
        if (previous === 'fix_now') return 'Still must fix';
        if (previous === 'fix_soon') return 'Now must fix';
    }
    if (grade === 'fix_soon') {
        if (previous === 'fix_soon') return 'Still fix soon';
        if (previous === 'fix_now') return 'Down to fix soon';
    }
    return GRADE_LABEL[grade];
}

/** "2nd inspection" for an item carried once, and so on. */
export function inspectionOrdinal(n: number): string {
    const tens = n % 100;
    const suffix = tens >= 11 && tens <= 13
        ? 'th'
        : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
    return `${n}${suffix} inspection`;
}

/** The checklist's label for an item key, or the key spelled out if the checklist lacks it. */
export function itemLabel(checklist: InspectionChecklist | null | undefined, item: string): string {
    const line = checklist?.lines.find((l) => l.key === item);
    if (line) return line.label;
    const words = item.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

// ── Calendar dates ───────────────────────────────────────────────────────────

/** "7 Dec". */
export function formatDay(ymd: string): string {
    return format(parseISO(ymd), 'd MMM');
}

/** "7 Dec 2026". */
export function formatDayYear(ymd: string): string {
    return format(parseISO(ymd), 'd MMM yyyy');
}

/** "December", for the round a period names. */
export function formatRoundMonth(period: string): string {
    return format(parseISO(period), 'MMMM');
}

/** "1–7 Dec", or "28 Nov – 4 Dec" for a window that crosses a month. */
export function formatWindow(opensOn: string, closesOn: string): string {
    const opens = parseISO(opensOn);
    const closes = parseISO(closesOn);
    if (format(opens, 'yyyy-MM') === format(closes, 'yyyy-MM')) {
        return `${format(opens, 'd')}–${format(closes, 'd MMM')}`;
    }
    return `${format(opens, 'd MMM')} – ${format(closes, 'd MMM')}`;
}

/** "45,210 km". */
export function formatKm(km: number): string {
    return `${km.toLocaleString('en-GB')} km`;
}
